import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { filter } from 'rxjs';

import { AccountService } from './account.service';

/** Centralizes all post-authentication bootstrap: account restoration, backend provisioning, navigation, and login/logout event handling. */
@Injectable({
  providedIn: 'root'
})
export class AuthBootstrapService {
  private readonly accountService = inject(AccountService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);

  initialize(): void {
    this.accountService.initializeAccountSummary();
    this.subscribeToAuthEvents();

    if (this.accountService.userIsLoggedIn()) {
      this.provisionUser();
      this.loadProfilePhoto();
    }
  }

  private subscribeToAuthEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGOUT_SUCCESS
        )
      )
      .subscribe(() => {
        if (this.accountService.userIsLoggedIn()) {
          this.accountService.initializeAccountSummary();
          this.provisionUser();
          this.loadProfilePhoto();
          this.router.navigate(['/', 'main', 'configuration']);
        } else {
          this.accountService.accountSummary.set(null);
          this.router.navigate(['/login']);
        }
      });
  }

  // After successful login, ensure the user exists in the backend and is provisioned with necessary permissions.
  // This is a no-op if the user already exists.
  private provisionUser(): void {
    const gid = this.accountService.accountSummary()?.gid;
    if (!gid) {
      return;
    }

    this.accountService.createUserIfDoesNotExist().subscribe({
      error: error => console.error('User provisioning failed:', error)
    });
  }

  private loadProfilePhoto(): void {
    this.accountService.getProfilePicture().subscribe(url => {
      this.accountService.profilePhotoUrl.set(url);
    });
  }
}
